import React, { useContext } from 'react'
import { InfoTooltip, PageWrapper } from '../../components'
import { toMenuOptions, WemoloAccordion, WemoloSearchableDropdown, WemoloToggle } from '../../components/Wemolo'
import { AutoWhiteList } from './AutoWhiteList'
import { StationConfig } from './StationConfig'
import { StationContext } from './StationContext'
import { StationInfo } from './StationInfo'
import { TrajectoryConfigurationContainer } from './TrajectoryConfiguration/TrajectoryConfigurationContainer'
import { transformStationDeviceToSelectValue, transformStationSelectValueToDevice } from './utils/transformSelectValues'
import LocalErrorBoundary from '../../components/LocalErrorBoundary'
import { SENTRYTAGS } from '../../logging'
import { DeviceTypeEnum } from '../../data/enums'
import { ODDisplayOverride, ODDisplayOverrideConfig } from './ODDisplayOverride'
import { AuditTargetType, ChangeLogView } from '../ChangeLog'
import { useFlags } from 'launchdarkly-react-client-sdk'
import styles from './StationView.module.css'

export const StationView = () => {
    const stationContext = useContext(StationContext)
    const { showStationEnabling } = useFlags()
    const stationType = stationContext.selectedStationData?.type
    const SCSDisplay = stationContext.selectedStationData?.device_uuid
        ? stationContext.selectedStationData?.device_uuid.split('_')[0] === 'scs'
        : false

    return (
        <PageWrapper>
            <div className={styles.headerRow}>
                <div className={styles.dropdownCol}>
                    <WemoloSearchableDropdown
                        placeholder="Select station"
                        options={toMenuOptions(stationContext.stations?.map(transformStationDeviceToSelectValue) || [])}
                        value={stationContext.selectedStation?.station_name}
                        onChange={(id) =>
                            stationContext.setSelectedStation(
                                transformStationSelectValueToDevice(id ? { id, label: id } : null)
                            )
                        }
                    />
                </div>
                {showStationEnabling && (
                    <div className={styles.toggleCol}>
                        <InfoTooltip
                            placement="left"
                            content={
                                <div className={styles.enabledTooltip}>
                                    A disabled station stops blocking its lot&apos;s event closure and no longer counts
                                    towards lot health. It keeps reporting its own device health.
                                </div>
                            }
                        >
                            <WemoloToggle
                                size="small"
                                label="Station enabled"
                                leftLabel
                                checked={stationContext.selectedStationData?.enabled ?? false}
                                disabled={!stationContext.selectedStation || stationContext.loadingUpdateStationEnabled}
                                onChange={(e) => {
                                    const nextEnabled = e.target.checked
                                    if (
                                        !nextEnabled &&
                                        !window.confirm(
                                            `Disable ${stationContext.selectedStation?.station_name}? Lots waiting on this station can close their events immediately, including events older than this change, and it will drop out of lot health.`
                                        )
                                    ) {
                                        return
                                    }
                                    stationContext.setStationEnabled(nextEnabled)
                                }}
                            />
                        </InfoTooltip>
                    </div>
                )}
            </div>
            <WemoloAccordion
                key={`station-info-${Boolean(stationContext.selectedStation)}`}
                title="Station Info"
                defaultExpanded={Boolean(stationContext.selectedStation)}
            >
                <LocalErrorBoundary tag={SENTRYTAGS.STATION}>
                    <StationInfo />
                </LocalErrorBoundary>
            </WemoloAccordion>
            {stationType === DeviceTypeEnum.SCANNER && (
                <LocalErrorBoundary tag={SENTRYTAGS.STATION}>
                    <TrajectoryConfigurationContainer />
                </LocalErrorBoundary>
            )}
            <WemoloAccordion
                key={`station-config-${Boolean(stationContext.selectedStation)}`}
                title="Station Configuration"
                defaultExpanded={Boolean(stationContext.selectedStation)}
            >
                <LocalErrorBoundary tag={SENTRYTAGS.STATION}>
                    <StationConfig
                        stationType={stationType}
                        saveStation={stationContext.saveStation}
                        deleteStation={stationContext.deleteStation}
                        devices={stationContext.devices}
                        initialDirection={stationContext.selectedStationData?.device_direction}
                        stationDeviceUUID={stationContext.selectedStationData?.device_uuid}
                    />
                </LocalErrorBoundary>
            </WemoloAccordion>
            {stationType === DeviceTypeEnum.SCANNER && (
                <WemoloAccordion
                    key={`auto-whitelist-${Boolean(stationContext.selectedStation)}`}
                    title="Auto Whitelist Configuration"
                    defaultExpanded={Boolean(stationContext.selectedStation)}
                >
                    <LocalErrorBoundary tag={SENTRYTAGS.STATION}>
                        <AutoWhiteList
                            loading={stationContext.loadingSaveStationConfig}
                            config={stationContext.selectedStationData?.config}
                            saveAutoWhitelistConfig={stationContext.saveStationConfig}
                        />
                    </LocalErrorBoundary>
                </WemoloAccordion>
            )}
            {stationType === DeviceTypeEnum.OCCUPANCY_DISPLAY && SCSDisplay && (
                <WemoloAccordion
                    key={`od-display-${Boolean(stationContext.selectedStation)}`}
                    title="Occupancy Display Configuration"
                    defaultExpanded={Boolean(stationContext.selectedStation)}
                >
                    <ODDisplayOverride
                        loading={stationContext.loadingSaveODDisplayOverride}
                        saveConfig={(config: ODDisplayOverrideConfig) =>
                            stationContext.saveODDisplayOverrideHandler(config)
                        }
                        config={stationContext.selectedStationData?.config}
                    />
                </WemoloAccordion>
            )}
            <WemoloAccordion
                key={`change-log-${Boolean(stationContext.selectedStation)}`}
                title="Change Log"
                defaultExpanded={Boolean(stationContext.selectedStation)}
            >
                <LocalErrorBoundary tag={SENTRYTAGS.STATION}>
                    <ChangeLogView
                        targetId={stationContext.selectedStation?.station_name}
                        targetType={AuditTargetType.station}
                    />
                </LocalErrorBoundary>
            </WemoloAccordion>
        </PageWrapper>
    )
}
